// src/lib/data/context/DataContext.js - V11 ENTERPRISE ULTRA-ROBUST
// ======================================================
// 🏢 DATA CONTEXT — SINGLETON MULTI-TENANT
// ======================================================
// 🔥 V11: DEDUPLICATION GUARD — Protección absoluta anti-duplicados
// 🔥 V11: dedupGuard intercepta create/save/batch en todas las colecciones
// 🔥 V11: cleanDuplicates() — Limpieza de duplicados existentes
// 🔥 V11: createCollectionProxy — Interceptado por DeduplicationGuard
// 🔥 V10: REALTIME SYNC — Sincronización en tiempo real entre dispositivos
// 🔥 V10: RealtimeSyncEngine integrado — cambios en < 1s entre navegadores
// 🔥 V10: startRealtimeSync() / stopRealtimeSync() — control granular
// 🔥 V10: destroy() limpia suscripciones de tiempo real
// 🔥 V9: INITIAL SYNC ENGINE — Sync completo multidispositivo (Netflix/Spotify Grade)
// 🔥 V9: loadInitialDataWithSync() — Stale-while-revalidate + progreso en tiempo real
// 🔥 V9: syncEngine expuesto para UI (progreso, estado, métricas)
// 🔥 V9: ZERO BREAKING CHANGES — API idéntica, comportamiento mejorado
// ======================================================
// ✅ Aislamiento por empresa (multi-tenant real)
// ✅ Singleton verdadero - getInstance es el ÚNICO punto de acceso
// ✅ Estado reactivo con listeners para UI inmediata
// ✅ Estrategia DUAL offline-first (Airbnb/QuintoAndar/Zillow grade)
// ✅ Initial Sync Engine — Nunca más datos incompletos en dispositivo nuevo
// ✅ Realtime Sync Engine — Cambios instantáneos entre dispositivos
// ✅ Deduplication Guard — Nunca más duplicados por contenido
// ✅ Zero breaking changes — misma API pública
// ======================================================

import { SyncManager } from '../sync/SyncManager.js';
import InitialSyncEngine from '../sync/InitialSyncEngine.js';
import RealtimeSync from '../sync/RealtimeSync.js';
import DeduplicationGuard from '../sync/DeduplicationGuard.js';
import LocalStrategy from '../strategies/LocalStrategy.js';
import CloudStrategy from '../strategies/CloudStrategy.js';
import DualSyncStrategy from '../strategies/DualSyncStrategy.js';
import { shouldUseFirebase, firebaseAvailable } from '../../firebase/config';
import authService from '../../../services/authService';

class DataContext {
  static instances = new Map();
  
  // ======================================================
  // 🔧 DEBUGGING Y HERRAMIENTAS DE DESARROLLO
  // ======================================================
  
  static exposeDebugging(instance) {
    if (typeof window === 'undefined' || !instance) return;
    
    window._dataContextDebug = instance;
    
    window.checkDataIsolation = () => {
      console.clear();
      console.log('=== 🔍 DIAGNÓSTICO AISLAMIENTO V11 ===');
      console.log(`👤 User ID: ${instance.userId}`);
      console.log(`🏢 Company ID: ${instance.companyId}`);
      console.log(`🎛️ Strategy: ${instance.strategyType}`);
      console.log(`🌐 Firebase: ${instance.firebaseAvailable ? '✅ Disponible' : '❌ No disponible'}`);
      console.log(`☁️ Cloud Strategy: ${!!instance.strategy?.cloud}`);
      console.log(`📁 Local Strategy: ${!!instance.strategy?.local}`);
      console.log(`🔄 Sync Manager: ${!!instance.syncManager}`);
      console.log(`🚀 Initial Sync Engine: ${!!instance.syncEngine}`);
      console.log(`📡 Realtime Sync Engine: ${!!instance.realtimeSync}`);
      console.log(`🛡️ Deduplication Guard: ${!!instance.dedupGuard}`);
      console.log(`🧹 Cache Invalidation: ACTIVO`);
      console.log(`🪦 Tombstone Protection: ACTIVO (triple-capa)`);
      console.log(`🛡️ Anti-Duplicados: ACTIVO (DeduplicationGuard V1)`);
      
      return {
        userId: instance.userId,
        companyId: instance.companyId,
        strategyType: instance.strategyType,
        firebaseAvailable: instance.firebaseAvailable,
        hasCloudStrategy: !!instance.strategy?.cloud,
        hasLocalStrategy: !!instance.strategy?.local,
        hasSyncEngine: !!instance.syncEngine,
        hasRealtimeSync: !!instance.realtimeSync,
        hasDedupGuard: !!instance.dedupGuard,
        realtimeActive: instance._realtimeActive,
        tombstoneProtection: true,
        antiDuplicates: true,
        cacheInvalidation: true,
        version: 'V11',
        dataCounts: {
          clients: instance._state.clients.length,
          properties: instance._state.properties.length,
          tasks: instance._state.tasks.length
        }
      };
    };
    
    window.checkUserData = async () => {
      console.log('📦 DATOS DEL USUARIO ACTUAL:');
      try {
        const clients = instance._state?.clients || [];
        console.log(`👥 Clientes: ${clients.length}`);
        clients.slice(0, 5).forEach((c, i) => {
          console.log(`  ${i+1}. ${c.name || 'Sin nombre'} (ID: ${c.id})`);
        });
        console.log(`🏢 Propiedades: ${instance._state?.properties?.length || 0}`);
        console.log(`✅ Tareas: ${instance._state?.tasks?.length || 0}`);
      } catch (e) {
        console.log('❌ Error:', e.message);
      }
    };
    
    window.showCurrentPath = () => {
      console.log(`📍 RUTA ACTUAL DE GUARDADO:`);
      console.log(`☁️ Firestore: companies/${instance.companyId}/`);
      console.log(`💾 Local: imoveispro_db_user_${instance.userId}`);
    };

    window.testInitialSync = async () => {
      console.log('🚀 TEST V11: Verificando Initial Sync Engine...');
      if (!instance.syncEngine) { console.log('❌ SyncEngine no inicializado'); return; }
      const diagnosis = await instance.syncEngine.needsFullSync('properties');
      console.log('📊 Diagnóstico:', diagnosis);
      const status = instance.syncEngine.getStatus();
      console.log('📊 Estado:', status);
      return { diagnosis, status };
    };

    window.testRealtimeSync = async () => {
      console.log('📡 TEST V11: Verificando Realtime Sync Engine...');
      if (!instance.realtimeSync) { console.log('❌ RealtimeSync no inicializado'); return; }
      return instance.realtimeSync.getStatus();
    };

    window.testDedupGuard = async () => {
      console.log('🛡️ TEST V11: Verificando Deduplication Guard...');
      if (!instance.dedupGuard) { console.log('❌ DedupGuard no inicializado'); return; }
      return instance.dedupGuard.getMetrics();
    };

    window.forceInitialSync = async () => {
      console.log('🚀 Forzando sync completo...');
      if (!instance.syncEngine) { console.log('❌ SyncEngine no inicializado'); return; }
      const result = await instance.syncEngine.fullSync('properties', (progress) => {
        console.log(`📊 Progreso: ${progress.phase} - ${progress.percent}% - ${progress.detail}`);
      });
      console.log('✅ Resultado:', result);
      await instance.loadInitialData(true);
      console.log(`📊 Datos post-sync: ${instance._state.properties.length} propiedades`);
      return result;
    };

    window.cleanDuplicates = async () => {
      console.log('🧹 Limpiando duplicados...');
      if (!instance.dedupGuard) { console.log('❌ DedupGuard no inicializado'); return; }
      const result = await instance.dedupGuard.cleanAll();
      console.log('✅ Resultado:', result);
      await instance.loadInitialData(true);
      console.log(`📊 Datos post-limpieza: ${instance._state.properties.length} propiedades`);
      return result;
    };
    
    window.testWriteClient = async () => {
      console.log('🧪 PROBANDO ESCRITURA...');
      try {
        const testClient = {
          name: `Test ${new Date().toISOString()}`,
          email: `test_${Date.now()}@test.com`,
          phone: '123456789',
          createdAt: new Date().toISOString(),
          userId: instance.userId,
          companyId: instance.companyId
        };
        const result = await instance.collections.clients.create(testClient);
        console.log(`✅ Cliente creado:`, result);
        console.log(`📊 Total clientes en estado: ${instance._state.clients.length}`);
        return { success: true, ...result };
      } catch (error) {
        console.error('❌ Error:', error);
        return { success: false, error: error.message };
      }
    };

    window.testDuplicateProtection = async () => {
      console.log('🛡️ TEST V11: Verificando protección anti-duplicados...');
      try {
        const countBefore = instance._state.clients.length;
        console.log(`📊 Clientes antes: ${countBefore}`);
        const result = await window.testWriteClient();
        if (!result.success) return;
        await instance.loadInitialData();
        const countAfter = instance._state.clients.length;
        console.log(`📊 Clientes después de recarga: ${countAfter}`);
        const ids = instance._state.clients.map(c => c.id);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
          console.log(`❌ FALLO: Hay ${ids.length - uniqueIds.size} IDs duplicados en el estado`);
        } else {
          console.log(`✅ ÉXITO V11: Sin duplicados. DeduplicationGuard funciona.`);
        }
        // Mostrar métricas del guard
        if (instance.dedupGuard) {
          console.log('📊 Métricas DedupGuard:', instance.dedupGuard.getMetrics());
        }
      } catch (e) {
        console.log('❌ Error:', e.message);
      }
    };

    window.testDeleteProtection = async () => {
      console.log('🪦 TEST V11: Verificando protección anti-resurrección...');
      try {
        const clients = instance._state?.clients || [];
        if (clients.length === 0) { console.log('⚠️ No hay clientes. Crea uno con testWriteClient()'); return; }
        const testClient = clients[0];
        console.log(`📋 Cliente a testear: ${testClient.name} (${testClient.id})`);
        console.log(`🗑️ Eliminando...`);
        await instance.collections.clients.delete(testClient.id);
        console.log(`📊 Estado inmediato: ${instance._state.clients.length} clientes`);
        console.log(`🔄 Recargando datos (simulando F5)...`);
        await instance.loadInitialData();
        console.log(`📊 Estado post-recarga: ${instance._state.clients.length} clientes`);
        const resurrected = instance._state.clients.find(
          c => c.id === testClient.id || c.uid === testClient.id || c._localId === testClient.id
        );
        if (resurrected) {
          console.log(`❌ FALLO: El cliente RESUCITÓ — ${resurrected.name}`);
        } else {
          console.log(`✅ ÉXITO V11: El cliente NO resucitó.`);
        }
      } catch (e) {
        console.log('❌ Error:', e.message);
      }
    };

    window.testCacheInvalidation = async () => {
      console.log('🧪 TEST V8: Verificando invalidación de caché...');
      try {
        const testProp = {
          title: `Test Cache ${Date.now()}`, type: 'Casa', status: 'Disponível', price: '100000',
          images: [`https://test.com/img_${Date.now()}.jpg`],
          previewImage: `https://test.com/preview_${Date.now()}.jpg`,
          createdAt: new Date().toISOString(), userId: instance.userId, companyId: instance.companyId
        };
        console.log('📝 Creando propiedad de prueba...');
        const created = await instance.collections.properties.create(testProp);
        console.log('✅ Creada:', created?.id);
        console.log('🔄 Refrescando datos...');
        await instance.refreshData();
        const found = instance._state.properties.find(p => p.id === created.id);
        if (found && found.images && found.images.length > 0) {
          console.log('✅ ÉXITO V8: La propiedad tiene imágenes después del refresh');
        } else {
          console.log('❌ FALLO: La propiedad perdió las imágenes después del refresh');
        }
        await instance.collections.properties.delete(created.id);
      } catch (e) {
        console.log('❌ Error:', e.message);
      }
    };
    
    console.log('✅ DataContext V11 expuesto para debugging');
  }
  
  // ======================================================
  // 🏗️ CONSTRUCTOR V11 — CON INITIAL SYNC + REALTIME SYNC + DEDUP GUARD
  // ======================================================
  
  constructor(userId, strategyType = 'auto') {
    this.userId = userId;
    this.firebaseAvailable = shouldUseFirebase();
    this.strategyType = this.detectStrategy(strategyType);
    this.companyId = this.getCorrectCompanyId(userId);
    this.user = this.getCompleteUserObject(userId, this.companyId);
    
    this._state = {
      clients: [],
      properties: [],
      tasks: [],
      loading: false,
      syncProgress: null,
      syncPhase: 'idle',
      error: null
    };
    
    this._listeners = [];
    this._realtimeActive = false;
    
    this.strategy = this.createStrategy(this.companyId, this.userId);
    
    if (this.strategy?.cloud?.setCompanyId) {
      this.strategy.cloud.setCompanyId(this.companyId);
    } else if (this.strategy?.setCompanyId) {
      this.strategy.setCompanyId(this.companyId);
    }
    
    this.syncManager = new SyncManager(userId, this.strategy);
    
    // 🔥 V9: InitialSyncEngine
    this.syncEngine = this._createSyncEngine();
    
    // 🔥 V10: RealtimeSync
    this.realtimeSync = this._createRealtimeSync();
    
    // 🔥 V11: DeduplicationGuard
    this.dedupGuard = this._createDedupGuard();
    
    this.initCollections();
    this.setupProxyMethods();
    
    console.log(
      `🚀 DataContext V11: ${userId} | Company: ${this.companyId} | ` +
      `Strategy: ${this.strategyType} | Firebase: ${this.firebaseAvailable ? '✅' : '❌'} | ` +
      `🚀 Initial Sync: ${!!this.syncEngine ? 'ACTIVO' : 'INACTIVO'} | ` +
      `📡 Realtime Sync: ${!!this.realtimeSync ? 'ACTIVO' : 'INACTIVO'} | ` +
      `🛡️ Dedup Guard: ${!!this.dedupGuard ? 'ACTIVO' : 'INACTIVO'} | ` +
      `🧹 Cache Invalidation: ACTIVO | 🪦 Anti-Resurrección: ACTIVO`
    );
    
    // Carga inicial con SWR
    setTimeout(() => this.loadInitialDataWithSync(), 10);
    
    DataContext.exposeDebugging(this);
  }

  // ======================================================
  // 🔥 V9: CREAR INITIAL SYNC ENGINE
  // ======================================================

  _createSyncEngine() {
    if (!this.strategy) return null;
    try {
      const localStrategy = this.strategy?.local || this.strategy;
      const cloudStrategy = this.strategy?.cloud || null;
      if (!cloudStrategy || !this.firebaseAvailable) {
        console.log('⏭️ InitialSyncEngine: No necesario (sin cloud)');
        return null;
      }
      const engine = new InitialSyncEngine({
        local: localStrategy, cloud: cloudStrategy,
        syncManager: this.syncManager, userId: this.userId, companyId: this.companyId,
      });
      console.log('🚀 InitialSyncEngine V1 creado y vinculado');
      return engine;
    } catch (error) {
      console.warn('⚠️ No se pudo crear InitialSyncEngine:', error.message);
      return null;
    }
  }

  // ======================================================
  // 🔥 V10: CREAR REALTIME SYNC ENGINE
  // ======================================================

  _createRealtimeSync() {
    if (!this.strategy) return null;
    try {
      const localStrategy = this.strategy?.local || this.strategy;
      const cloudStrategy = this.strategy?.cloud || null;
      if (!cloudStrategy || !this.firebaseAvailable) {
        console.log('⏭️ RealtimeSync: No necesario (sin cloud)');
        return null;
      }
      const engine = new RealtimeSync({
        local: localStrategy, cloud: cloudStrategy,
        syncManager: this.syncManager, userId: this.userId, companyId: this.companyId,
      });
      console.log('📡 RealtimeSync V1 creado y vinculado');
      return engine;
    } catch (error) {
      console.warn('⚠️ No se pudo crear RealtimeSync:', error.message);
      return null;
    }
  }

  // ======================================================
  // 🔥 V11: CREAR DEDUPLICATION GUARD
  // ======================================================

  _createDedupGuard() {
    if (!this.strategy) return null;
    try {
      const localStrategy = this.strategy?.local || this.strategy;
      const cloudStrategy = this.strategy?.cloud || null;
      const engine = new DeduplicationGuard({
        local: localStrategy, cloud: cloudStrategy,
        userId: this.userId, companyId: this.companyId,
      });
      console.log('🛡️ DeduplicationGuard V1 creado y vinculado');
      return engine;
    } catch (error) {
      console.warn('⚠️ No se pudo crear DeduplicationGuard:', error.message);
      return null;
    }
  }

  // ======================================================
  // 🔥 V10: INICIAR / DETENER SINCRONIZACIÓN EN TIEMPO REAL
  // ======================================================

  startRealtimeSync(collections = null) {
    if (!this.realtimeSync) {
      console.warn('⚠️ [V11] RealtimeSync no disponible');
      return { success: false, error: 'realtime_sync_unavailable' };
    }
    if (this._realtimeActive) {
      console.log('⏭️ [V11] RealtimeSync ya está activo');
      return { success: true, alreadyActive: true };
    }
    const result = this.realtimeSync.start(collections);
    if (result.success) {
      this._realtimeActive = true;
      this._setupRealtimeListeners();
      console.log('📡 [V11] Sincronización en tiempo real ACTIVADA');
    }
    return result;
  }

  stopRealtimeSync() {
    if (!this.realtimeSync) return { success: false, error: 'realtime_sync_unavailable' };
    this.realtimeSync.stop();
    this._realtimeActive = false;
    console.log('📡 [V11] Sincronización en tiempo real DESACTIVADA');
    return { success: true };
  }

  _setupRealtimeListeners() {
    if (!this.realtimeSync) return;
    this.realtimeSync.subscribe((event) => {
      if (event.type === 'dataChanged') this._handleRealtimeDataChange(event);
      else if (event.type === 'connectionChange') this._handleRealtimeConnectionChange(event);
    });
    if (typeof window !== 'undefined') {
      const handleDataChanged = (event) => {
        const { collection } = event.detail || {};
        if (collection === 'properties' || collection === 'clients' || collection === 'tasks') {
          this._reloadCollection(collection);
        }
      };
      window.addEventListener('realtime:dataChanged', handleDataChanged);
      this._realtimeDOMListeners = this._realtimeDOMListeners || [];
      this._realtimeDOMListeners.push({ event: 'realtime:dataChanged', handler: handleDataChanged });
    }
  }

  _handleRealtimeDataChange(event) {
    const { collection } = event;
    if (!collection) return;
    console.log(`📡 [V11] Cambio en tiempo real: ${collection}`);
    this._reloadCollection(collection);
  }

  _handleRealtimeConnectionChange(event) {
    const { connected } = event;
    console.log(`📡 [V11] Conexión tiempo real: ${connected ? 'CONECTADO' : 'DESCONECTADO'}`);
  }

  async _reloadCollection(collectionName) {
    try {
      const data = await this.collections[collectionName]?.read() || [];
      if (this._state[collectionName]) {
        this._state[collectionName] = data;
        this._notifyListeners();
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new CustomEvent('datacontext:realtimeUpdate', {
            detail: { collection: collectionName, count: data.length }
          }));
        }
      }
    } catch (error) {
      console.warn(`⚠️ [V11] Error recargando ${collectionName}:`, error.message);
    }
  }
  
  // ======================================================
  // 📡 SISTEMA DE LISTENERS (REACTIVIDAD)
  // ======================================================
  
  _notifyListeners() {
    this._listeners.forEach(listener => {
      try { listener({ ...this._state }); } catch (e) {}
    });
  }
  
  subscribe(listener) {
    this._listeners.push(listener);
    return () => { this._listeners = this._listeners.filter(l => l !== listener); };
  }
  
  // ======================================================
  // 👤 OBTENER OBJETO USER COMPLETO
  // ======================================================
  
  getCompleteUserObject(userId, companyId) {
    try {
      const authUserStr = localStorage.getItem('auth_user');
      if (authUserStr) {
        const authUser = JSON.parse(authUserStr);
        if (authUser.id === userId || authUser.uid === userId) {
          return {
            id: authUser.id || userId, uid: authUser.uid || userId,
            email: authUser.email, name: authUser.name,
            role: authUser.role || 'admin',
            companyId: authUser.companyId || companyId,
            company: authUser.company || companyId,
            canWrite: authUser.canWrite !== false, canDelete: true,
            canRead: authUser.canRead !== false,
            _isAuthenticated: true, _securityLevel: authUser.role || 'admin',
            _tenantId: authUser.companyId || companyId,
            createdAt: authUser.createdAt || new Date().toISOString(),
            updatedAt: authUser.updatedAt || new Date().toISOString()
          };
        }
      }
    } catch (e) { console.warn('⚠️ Error obteniendo user de auth_user:', e); }
    return {
      id: userId, uid: userId, companyId: companyId,
      email: `${userId}@user.com`, role: 'admin',
      canWrite: true, canDelete: true, canRead: true,
      _isAuthenticated: true, _securityLevel: 'admin', _tenantId: companyId,
      createdAt: new Date().toISOString()
    };
  }
  
  // ======================================================
  // 🏢 OBTENER COMPANY ID CORRECTO
  // ======================================================
  
  getCorrectCompanyId(userId) {
    try {
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      if (authUser.companyId?.startsWith('company_')) return authUser.companyId;
      if (authUser.company?.startsWith('company_')) return authUser.company;
    } catch (e) {}
    const currentCompany = localStorage.getItem('current_company');
    if (currentCompany?.startsWith('company_')) return currentCompany;
    return `company_${userId}`;
  }
  
  // ======================================================
  // 🎯 DETECCIÓN DE ESTRATEGIA
  // ======================================================
  
  detectStrategy(requestedStrategy) {
    if (requestedStrategy && requestedStrategy !== 'auto' && ['local', 'cloud', 'dual'].includes(requestedStrategy)) {
      console.log(`📌 Estrategia explícita: ${requestedStrategy}`);
      return requestedStrategy;
    }
    const savedStrategy = localStorage.getItem('data_strategy');
    if (savedStrategy && ['local', 'cloud', 'dual'].includes(savedStrategy)) {
      console.log(`📌 Estrategia guardada: ${savedStrategy}`);
      return savedStrategy;
    }
    if (authService?.getMode() === 'demo') { console.log('📌 Modo DEMO → LOCAL'); return 'local'; }
    if (!this.firebaseAvailable || !navigator.onLine) { console.log('📌 Sin Firebase → LOCAL'); return 'local'; }
    console.log('📌 Firebase disponible → DUAL (Enterprise V11)');
    return 'dual';
  }
  
  // ======================================================
  // 🔧 CREAR ESTRATEGIA
  // ======================================================
  
  createStrategy(companyId, userId) {
    console.log(`🔧 Creando estrategia: ${this.strategyType} | User: ${userId} | Company: ${companyId}`);
    switch (this.strategyType) {
      case 'local': return new LocalStrategy(companyId, userId);
      case 'cloud':
        try { return new CloudStrategy(this.user); }
        catch (error) { console.error('❌ CloudStrategy falló:', error.message); return new LocalStrategy(companyId, userId); }
      case 'dual': case 'auto': default:
        try { return new DualSyncStrategy(companyId, userId); }
        catch (error) {
          console.error('❌ DualSyncStrategy falló:', error.message);
          try { return new CloudStrategy(this.user); }
          catch (cloudError) { return new LocalStrategy(companyId, userId); }
        }
    }
  }
  
  // ======================================================
  // 🪦 FILTRO ANTI-RESURRECCIÓN
  // ======================================================
  
  _filterTombstones(items) {
    if (!Array.isArray(items)) return [];
    return items.filter(item => {
      if (item._deleted === true) return false;
      if (item._isDeleted === true) return false;
      if (item.tombstone === true) return false;
      if (item.deleted === true) return false;
      if (item.deletedAt && new Date(item.deletedAt).getTime() > 0) return false;
      return true;
    });
  }
  
  _filterAndDedupe(items) {
    const alive = this._filterTombstones(items);
    return this._dedupeById(alive);
  }
  
  _invalidateStrategyCache(collectionName) {
    try {
      if (this.strategy?.invalidateCache) this.strategy.invalidateCache(collectionName);
      if (this.strategy?.local?.invalidateCache) this.strategy.local.invalidateCache(collectionName);
      if (this.strategy?.cloud?.invalidateCache) this.strategy.cloud.invalidateCache(collectionName);
    } catch (e) {}
  }
  
  // ======================================================
  // 📥 CARGA DE DATOS
  // ======================================================
  
  async loadInitialData(forceFresh = false) {
    console.log(`📥 Cargando datos iniciales V11${forceFresh ? ' (forceFresh)' : ''}...`);
    this._state.loading = true;
    this._notifyListeners();
    try {
      if (forceFresh) {
        this._invalidateStrategyCache('clients');
        this._invalidateStrategyCache('properties');
        this._invalidateStrategyCache('tasks');
      }
      const [clients, properties, tasks] = await Promise.all([
        this.collections.clients.read().catch(() => []),
        this.collections.properties.read().catch(() => []),
        this.collections.tasks.read().catch(() => [])
      ]);
      this._state.clients = this._filterAndDedupe(clients);
      this._state.properties = this._filterAndDedupe(properties);
      this._state.tasks = this._filterAndDedupe(tasks);
      console.log(
        `✅ Datos cargados V11: ${this._state.clients.length} clientes, ` +
        `${this._state.properties.length} propiedades, ${this._state.tasks.length} tareas`
      );
    } catch (error) {
      console.error('❌ Error cargando datos:', error);
      this._state.error = error.message;
    } finally {
      this._state.loading = false;
      this._notifyListeners();
    }
  }

  async loadInitialDataWithSync() {
    console.log('📥 [V11] loadInitialDataWithSync — Stale-While-Revalidate');
    this._state.loading = true;
    this._state.syncPhase = 'loading';
    this._notifyListeners();
    try {
      const [clients, properties, tasks] = await Promise.all([
        this.collections.clients.read().catch(() => []),
        this.collections.properties.read().catch(() => []),
        this.collections.tasks.read().catch(() => [])
      ]);
      this._state.clients = this._filterAndDedupe(clients);
      this._state.properties = this._filterAndDedupe(properties);
      this._state.tasks = this._filterAndDedupe(tasks);
      this._state.loading = false;
      this._notifyListeners();
      console.log(
        `📦 [V11] Datos locales: ${this._state.clients.length} clientes, ` +
        `${this._state.properties.length} propiedades, ${this._state.tasks.length} tareas`
      );
      if (this.syncEngine && this.firebaseAvailable && navigator.onLine) {
        await this._backgroundSyncCheck();
      } else {
        this._state.syncPhase = 'complete';
        this._notifyListeners();
      }
      if (this.realtimeSync && this.firebaseAvailable && navigator.onLine && !this._realtimeActive) {
        setTimeout(() => { console.log('📡 [V11] Iniciando tiempo real post-carga...'); this.startRealtimeSync(); }, 2000);
      }
    } catch (error) {
      console.error('❌ [V11] Error en loadInitialDataWithSync:', error);
      this._state.error = error.message;
      this._state.loading = false;
      this._state.syncPhase = 'error';
      this._notifyListeners();
    }
  }

  async _backgroundSyncCheck() {
    try {
      this._state.syncPhase = 'checking';
      this._notifyListeners();
      const diagnosis = await this.syncEngine.needsFullSync('properties');
      console.log(`🔍 [V11] Diagnóstico: ${diagnosis.needsSync ? 'NECESITA SYNC' : 'OK'} | Local: ${diagnosis.localCount} | Cloud: ${diagnosis.cloudCount}`);
      if (diagnosis.needsSync && diagnosis.urgency !== 'low') await this._executeInitialSync(diagnosis);
      else if (diagnosis.needsSync && diagnosis.urgency === 'low') this._executeBackgroundRefresh();
      else { this._state.syncPhase = 'complete'; this._notifyListeners(); console.log('✅ [V11] Datos frescos'); }
    } catch (error) {
      console.warn('⚠️ [V11] Error en background sync check:', error.message);
      this._state.syncPhase = 'complete'; this._notifyListeners();
    }
  }

  async _executeInitialSync(diagnosis) {
    console.log(`🚀 [V11] Sync completo — ${diagnosis.cloudCount} docs en cloud`);
    this._state.syncPhase = 'syncing';
    this._state.syncProgress = { phase: 'starting', percent: 0, detail: 'Sincronizando...', collection: 'properties' };
    this._notifyListeners();
    try {
      const result = await this.syncEngine.fullSync('properties', (progress) => {
        this._state.syncProgress = { ...progress, collection: 'properties' };
        this._notifyListeners();
      });
      console.log(`✅ [V11] Sync completado: ${result.documentsSaved} docs en ${Math.round(result.elapsed)}ms`);
      await this.loadInitialData(true);
      this._state.syncPhase = 'complete';
      this._state.syncProgress = { phase: 'complete', percent: 100, detail: `${this._state.properties.length} inmuebles` };
      this._notifyListeners();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('datacontext:syncComplete', {
          detail: { collection: 'properties', count: this._state.properties.length }
        }));
      }
    } catch (error) {
      console.error('❌ [V11] Error en sync:', error.message);
      this._state.syncPhase = 'error';
      this._state.syncProgress = { phase: 'error', percent: 100, detail: `Error: ${error.message}` };
      this._notifyListeners();
    }
  }

  _executeBackgroundRefresh() {
    console.log('🔄 [V11] Background refresh...');
    this.syncEngine.fullSync('properties', (progress) => {
      if (progress.phase === 'complete') {
        this.loadInitialData(true).then(() => {
          this._state.syncPhase = 'complete'; this._notifyListeners();
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('datacontext:syncComplete', {
              detail: { collection: 'properties', count: this._state.properties.length }
            }));
          }
        });
      }
    }).catch(err => {
      console.warn('⚠️ [V11] Background refresh falló:', err.message);
      this._state.syncPhase = 'complete'; this._notifyListeners();
    });
    this._state.syncPhase = 'complete'; this._notifyListeners();
  }
  
  _dedupeById(items) {
    if (!Array.isArray(items)) return [];
    const seen = new Set();
    return items.filter(item => {
      const id = String(item?.id || item?.uid || '');
      if (!id || seen.has(id)) return false;
      seen.add(id); return true;
    });
  }
  
  _findItemIndex(items, id) {
    const strId = String(id);
    return items.findIndex(item => 
      String(item.id) === strId || String(item.uid) === strId || String(item._localId) === strId
    );
  }
  
  // ======================================================
  // 📦 COLECCIONES — V11 CON DEDUPLICATION GUARD
  // ======================================================
  
  initCollections() {
    this.collections = {
      users: this.createCollectionProxy('users'),
      clients: this.createCollectionProxy('clients'),
      properties: this.createCollectionProxy('properties'),
      tasks: this.createCollectionProxy('tasks'),
      calendarEvents: this.createCollectionProxy('calendarEvents'),
      documents: this.createCollectionProxy('documents'),
      financeiro: this.createCollectionProxy('financeiro'),
      interactions: this.createCollectionProxy('interactions'),
      messages: this.createCollectionProxy('messages'),
      // 👥 Multi-user team (scale-ready)
      team_members: this.createCollectionProxy('team_members'),
      team_invites: this.createCollectionProxy('team_invites'),
    };
  }
  
  createCollectionProxy(collectionName) {
    const context = this;
    
    return {
      // 🔥 V11: CREATE interceptado por DeduplicationGuard
      create: async (data) => {
        // Verificar duplicados antes de crear
        if (context.dedupGuard) {
          const guardResult = await context.dedupGuard.interceptCreate(collectionName, data);
          if (guardResult.intercepted) {
            // Documento ya existe, retornar el mergeado
            if (context._state[collectionName] && guardResult.existingId) {
              const index = context._findItemIndex(context._state[collectionName], guardResult.existingId);
              if (index >= 0) {
                context._state[collectionName][index] = { 
                  ...context._state[collectionName][index], 
                  ...data, 
                  ...guardResult.result 
                };
                context._notifyListeners();
              }
            }
            return guardResult.result;
          }
        }
        
        // No hay duplicado, crear normalmente
        const result = await context.strategy.create(collectionName, data);
        const newId = result?.id || result?.uid;
        context._invalidateStrategyCache(collectionName);
        
        if (context._state[collectionName] && newId) {
          const existingIndex = context._findItemIndex(context._state[collectionName], newId);
          if (existingIndex >= 0) {
            context._state[collectionName][existingIndex] = { 
              ...context._state[collectionName][existingIndex], ...data, ...result 
            };
          } else {
            context._state[collectionName] = [...context._state[collectionName], { ...data, ...result, id: newId }];
          }
          context._notifyListeners();
        }
        return result;
      },
      
      read: async (query = {}) => {
        try {
          const result = await context.strategy.read(collectionName, query);
          return context._filterAndDedupe(Array.isArray(result) ? result : []);
        } catch (error) {
          console.error(`❌ Error leyendo ${collectionName}:`, error.message);
          return context._state[collectionName] || [];
        }
      },
      
      update: async (id, data) => {
        const result = await context.strategy.update(collectionName, id, data);
        context._invalidateStrategyCache(collectionName);
        if (context._state[collectionName]) {
          const index = context._findItemIndex(context._state[collectionName], id);
          if (index >= 0) {
            context._state[collectionName][index] = { 
              ...context._state[collectionName][index], ...data, ...result 
            };
          }
          context._notifyListeners();
        }
        return result;
      },
      
      delete: async (id) => {
        if (context._state[collectionName]) {
          const index = context._findItemIndex(context._state[collectionName], id);
          if (index >= 0) {
            context._state[collectionName][index] = {
              ...context._state[collectionName][index],
              _deleted: true, _isDeleted: true, deletedAt: new Date().toISOString()
            };
          }
        }
        const result = await context.strategy.delete(collectionName, id);
        context._invalidateStrategyCache(collectionName);
        if (context._state[collectionName]) {
          const strId = String(id);
          context._state[collectionName] = context._state[collectionName].filter(
            item => String(item.id) !== strId && String(item.uid) !== strId && String(item._localId) !== strId
          );
          context._notifyListeners();
        }
        return result;
      },
      
      save: async (data, id = null) => {
        // 🔥 V11: SAVE también interceptado por DeduplicationGuard (solo si es create)
        if (!id && context.dedupGuard) {
          const guardResult = await context.dedupGuard.interceptCreate(collectionName, data);
          if (guardResult.intercepted) {
            if (context._state[collectionName] && guardResult.existingId) {
              const index = context._findItemIndex(context._state[collectionName], guardResult.existingId);
              if (index >= 0) {
                context._state[collectionName][index] = { 
                  ...context._state[collectionName][index], ...data, ...guardResult.result 
                };
                context._notifyListeners();
              }
            }
            return guardResult.result;
          }
        }
        
        const saveFn = context.strategy.save;
        const result = saveFn 
          ? await saveFn.call(context.strategy, collectionName, data, id)
          : id 
            ? await context.strategy.update(collectionName, id, data)
            : await context.strategy.create(collectionName, data);
        
        context._invalidateStrategyCache(collectionName);
        const resultId = result?.id || result?.uid || id;
        if (context._state[collectionName] && resultId) {
          const existingIndex = context._findItemIndex(context._state[collectionName], resultId);
          if (existingIndex >= 0) {
            context._state[collectionName][existingIndex] = { 
              ...context._state[collectionName][existingIndex], ...data, ...result 
            };
          } else if (!id || existingIndex < 0) {
            context._state[collectionName] = [...context._state[collectionName], { ...data, ...result, id: resultId }];
          }
          context._notifyListeners();
        }
        return result;
      },
      
      findById: async (id) => {
        const local = context._state[collectionName]?.find(
          item => String(item.id) === String(id) || String(item.uid) === String(id)
        );
        if (local) return local;
        try {
          context._invalidateStrategyCache(collectionName);
          const items = await context.strategy.read(collectionName, {
            where: [{ field: 'id', operator: '==', value: id }]
          });
          return items?.[0] || null;
        } catch (e) { return null; }
      },
      
      findByField: async (field, value) => {
        try {
          const result = await context.strategy.read(collectionName, {
            where: [{ field, operator: '==', value }]
          });
          return context._filterAndDedupe(Array.isArray(result) ? result : []);
        } catch (e) { return []; }
      },
      
      count: async () => { return context._state[collectionName]?.length || 0; }
    };
  }
  
  // ======================================================
  // 🔧 MÉTODOS PROXY (COMPATIBILIDAD) + V11
  // ======================================================
  
  setupProxyMethods() {
    this.getClients = (query) => this.collections.clients.read(query);
    this.getClientById = (id) => this._state.clients?.find(c => c.id === id || c.uid === id) || null;
    this.createClient = (data) => this.collections.clients.create(data);
    this.updateClient = (id, data) => this.collections.clients.update(id, data);
    this.deleteClient = (id) => this.collections.clients.delete(id);
    this.saveClient = (data, id) => this.collections.clients.save(data, id);
    
    this.getProperties = (query) => this.collections.properties.read(query);
    this.getPropertyById = (id) => this._state.properties?.find(p => p.id === id || p.uid === id) || null;
    this.createProperty = (data) => this.collections.properties.create(data);
    this.updateProperty = (id, data) => this.collections.properties.update(id, data);
    this.deleteProperty = (id) => this.collections.properties.delete(id);
    this.saveProperty = (data, id) => this.collections.properties.save(data, id);
    
    this.getUsers = (query) => this.collections.users.read(query);
    this.createUser = (data) => this.collections.users.create(data);
    this.updateUser = (id, data) => this.collections.users.update(id, data);
    this.deleteUser = (id) => this.collections.users.delete(id);
    
    this.getTasksByClient = async (clientId) => {
      try { return await this.strategy.getTasksByClient?.(clientId, this.userId) || []; }
      catch (e) { return this.collections.tasks.findByField('clientId', clientId); }
    };
    
    this.getDashboardStats = async () => {
      try { return await this.strategy.getDashboardStats?.(this.userId) || {
        activeProperties: this._state.properties.length,
        activeClients: this._state.clients.length,
        pendingTasks: this._state.tasks.length
      };}
      catch (e) { return {
        activeProperties: this._state.properties.length,
        activeClients: this._state.clients.length,
        pendingTasks: this._state.tasks.length
      };}
    };
    
    this.getState = () => ({ ...this._state });
    this.refreshData = async (forceFresh = false) => { await this.loadInitialData(forceFresh); return this.getState(); };
    
    this.setCompanyId = (companyId) => {
      this.companyId = companyId;
      localStorage.setItem('current_company', companyId);
      if (this.strategy?.setCompanyId) this.strategy.setCompanyId(companyId);
      else if (this.strategy?.cloud?.setCompanyId) this.strategy.cloud.setCompanyId(companyId);
      this.refreshData(true);
    };
    
    this.setUserId = (userId) => { this.userId = userId; this.user.id = userId; this.user.uid = userId; this.refreshData(true); };
    this.setMode = async (mode) => this.setStrategy(mode);
    
    this.healthCheck = () => ({
      version: 'V11',
      strategyType: this.strategyType,
      firebaseAvailable: this.firebaseAvailable,
      userId: this.userId, companyId: this.companyId,
      hasCloudStrategy: !!this.strategy?.cloud,
      hasLocalStrategy: !!this.strategy?.local,
      hasSyncEngine: !!this.syncEngine,
      syncEngineStatus: this.syncEngine?.getStatus() || null,
      hasRealtimeSync: !!this.realtimeSync,
      realtimeActive: this._realtimeActive,
      realtimeStatus: this.realtimeSync?.getStatus() || null,
      hasDedupGuard: !!this.dedupGuard,
      dedupGuardMetrics: this.dedupGuard?.getMetrics() || null,
      tombstoneProtection: true, tombstoneFilterActive: true,
      antiDuplicates: true, antiResurrection: true,
      cacheInvalidation: true, tripleLayer: true,
      offlineFirst: this.strategyType === 'dual',
      syncManager: this.syncManager?.getStatus?.() || null,
      dataCounts: {
        clients: this._state.clients.length,
        properties: this._state.properties.length,
        tasks: this._state.tasks.length
      }
    });
    
    this.syncNow = async () => this.syncManager?.processQueue?.() || { success: false };
    this.getSyncStatus = () => this.syncManager?.getStatus?.() || { available: false };
    this.clearSyncQueue = () => { this.syncManager?.clearQueue?.(); return { success: true }; };
    
    // V9: Initial Sync Engine
    this.getSyncEngine = () => this.syncEngine;
    this.getSyncProgress = () => this._state.syncProgress;
    this.forceFullSync = async (collectionName = 'properties') => {
      if (!this.syncEngine) return { success: false, error: 'SyncEngine no disponible' };
      this._state.syncPhase = 'syncing'; this._notifyListeners();
      const result = await this.syncEngine.fullSync(collectionName, (progress) => {
        this._state.syncProgress = progress; this._notifyListeners();
      });
      if (result.success || result.partial) await this.loadInitialData(true);
      this._state.syncPhase = 'complete'; this._notifyListeners();
      return result;
    };
    this.checkNeedsSync = async (collectionName = 'properties') => {
      if (!this.syncEngine) return { needsSync: false, reason: 'no_sync_engine' };
      return this.syncEngine.needsFullSync(collectionName);
    };
    
    // V10: Realtime Sync
    this.getRealtimeSync = () => this.realtimeSync;
    this.isRealtimeActive = () => this._realtimeActive;
    
    // 🔥 V11: Deduplication Guard
    this.getDedupGuard = () => this.dedupGuard;
    this.cleanDuplicates = async (collectionName = null) => {
      if (!this.dedupGuard) return { success: false, error: 'DedupGuard no disponible' };
      const result = collectionName 
        ? await this.dedupGuard.cleanCollection(collectionName)
        : await this.dedupGuard.cleanAll();
      if (result.success || result.totalRemoved > 0) await this.loadInitialData(true);
      return result;
    };
    
    // V8: Helpers de caché
    this.invalidateCache = (collectionName) => {
      this._invalidateStrategyCache(collectionName);
      return { success: true, collection: collectionName };
    };
    this.invalidateAllCaches = () => {
      const collections = ['clients', 'properties', 'tasks', 'calendarEvents', 'documents', 'financeiro', 'messages', 'interactions', 'users'];
      collections.forEach(col => this._invalidateStrategyCache(col));
      return { success: true, collections };
    };
  }
  
  // ======================================================
  // 🔄 CAMBIO DE ESTRATEGIA
  // ======================================================
  
  async setStrategy(strategyType) {
    console.log(`🔄 Cambiando estrategia a: ${strategyType}`);
    if (this._realtimeActive) this.stopRealtimeSync();
    this.strategyType = strategyType;
    this.user = this.getCompleteUserObject(this.userId, this.companyId);
    this.strategy = this.createStrategy(this.companyId, this.userId);
    if (this.syncManager) this.syncManager.setStrategy(this.strategy);
    this.syncEngine = this._createSyncEngine();
    this.realtimeSync = this._createRealtimeSync();
    this.dedupGuard = this._createDedupGuard();
    localStorage.setItem('data_strategy', strategyType);
    this.setupProxyMethods();
    await this.refreshData(true);
    if (this.realtimeSync && this.firebaseAvailable && navigator.onLine) {
      setTimeout(() => this.startRealtimeSync(), 1000);
    }
    console.log(`✅ Estrategia cambiada a: ${strategyType}`);
    return { success: true, newStrategy: strategyType };
  }
  
  // ======================================================
  // 📤 GETTERS
  // ======================================================
  
  getStrategy() { return this.strategy; }
  getUser() { return this.user; }
  
  // ======================================================
  // ♻️ DESTRUCCIÓN
  // ======================================================
  
  destroy() {
    if (this.realtimeSync) { this.stopRealtimeSync(); this.realtimeSync.destroy(); }
    if (this.dedupGuard) this.dedupGuard.destroy();
    if (this._realtimeDOMListeners && typeof window !== 'undefined') {
      this._realtimeDOMListeners.forEach(({ event, handler }) => window.removeEventListener(event, handler));
      this._realtimeDOMListeners = [];
    }
    if (this.syncEngine) this.syncEngine.abort();
    if (this.syncManager) this.syncManager.destroy();
    if (typeof window !== 'undefined') {
      delete window.checkDataIsolation; delete window.checkUserData;
      delete window.showCurrentPath; delete window.testInitialSync;
      delete window.testRealtimeSync; delete window.testDedupGuard;
      delete window.forceInitialSync; delete window.cleanDuplicates;
      delete window.testWriteClient; delete window.testDeleteProtection;
      delete window.testDuplicateProtection; delete window.testCacheInvalidation;
      delete window._dataContextDebug;
    }
    console.log(`♻️ DataContext V11 destruido: ${this.userId}`);
  }
  
  // ======================================================
  // 🔥 SINGLETON - PUNTO DE ACCESO ÚNICO
  // ======================================================
  
  static getInstance(userId, strategyType = 'auto') {
    if (DataContext.instances.has(userId)) {
      const instance = DataContext.instances.get(userId);
      console.log(`🔄 Reutilizando DataContext V11: ${userId}`);
      DataContext.exposeDebugging(instance);
      return instance;
    }
    console.log(`🆕 Creando DataContext V11: ${userId}`);
    const instance = new DataContext(userId, strategyType);
    DataContext.instances.set(userId, instance);
    DataContext.exposeDebugging(instance);
    return instance;
  }
  
  static clearInstance(userId) {
    if (DataContext.instances.has(userId)) {
      const instance = DataContext.instances.get(userId);
      instance.destroy();
      DataContext.instances.delete(userId);
      return true;
    }
    return false;
  }
  
  static setCurrentUser(userId, userEmail, companyId = null) {
    localStorage.setItem('userEmail', userEmail);
    try {
      const authUser = JSON.parse(localStorage.getItem('auth_user') || '{}');
      const finalCompanyId = companyId || authUser.companyId || `company_${userId}`;
      const authUserData = {
        ...authUser, id: userId, uid: userId, email: userEmail,
        companyId: finalCompanyId, company: finalCompanyId,
        _tenantId: finalCompanyId, _isAuthenticated: true
      };
      localStorage.setItem('auth_user', JSON.stringify(authUserData));
      localStorage.setItem('current_company', finalCompanyId);
      console.log(`👤 Usuario establecido: ${userEmail} → ${finalCompanyId}`);
      return { user: authUserData, companyId: finalCompanyId };
    } catch (e) { console.warn('⚠️ Error en setCurrentUser:', e); return null; }
  }
  
  static reset() {
    DataContext.instances.forEach(i => { if (i.destroy) i.destroy(); });
    DataContext.instances.clear();
    if (typeof window !== 'undefined') {
      delete window.checkDataIsolation; delete window.checkUserData;
      delete window.showCurrentPath; delete window.testInitialSync;
      delete window.testRealtimeSync; delete window.testDedupGuard;
      delete window.forceInitialSync; delete window.cleanDuplicates;
      delete window.testWriteClient; delete window.testDeleteProtection;
      delete window.testDuplicateProtection; delete window.testCacheInvalidation;
      delete window._dataContextDebug;
    }
    console.log('🔄 DataContext V11 reseteado');
  }
}

export default DataContext;
export { DataContext };